/**
 * File: src/Pages/GMS/ISR/Components/EmbedCodeDialog.tsx
 *
 * The Embed code dialog for a Guide channel's Widgets panel (ADO #14203 —
 * Host Integration: Self-Service Embed Code). Opens at PANEL level — one
 * dialog per channel, covering every widget — replacing the earlier
 * per-widget dialog opened from each widget's own row (ADO #14214 US-2 AC 1).
 *
 * Three tabs, each a distinct surface:
 *   Site code           — the channel-wide Script + HTML blocks. One per
 *                          channel; every widget appears in the Script's
 *                          dcConfig. Platform-aware (Custom / Shopify /
 *                          BigCommerce / Magento 2).
 *   Widget placements    — a snippet GENERATOR: widget + trigger + starting
 *                          state in, one placement snippet out. Nothing here
 *                          is saved — the same widget can be placed
 *                          differently on different pages (Jabir, 10 Sept).
 *   Input parameters      — per-widget VDA pre-fill block, for widgets whose
 *                          model declares Input Parameters.
 *
 * All three read from `embedSnippet.ts`, which owns the runtime contract —
 * see its file-level comment for the platform-namespace resolution
 * (`compatioConfig[platForm]`, not always `.custom`) and the two open items
 * ADO #14214 (US-2) left for the runtime team.
 *
 * Copyright (c) 2026 Compatio LLC, Springfield, MO - www.compatio.ai
 * Protected by the Trade Secrets Act.
 */
import React, { useEffect, useMemo, useState } from "react";
import Alert from 'Components/DesignSystem/Alert/Alert';
import Button from 'Components/DesignSystem/Button/Button';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import { FOCUS_RING } from 'Components/DesignSystem/focusRing';
import { Copy } from 'Components/DesignSystem/Iconography/Icons';
import SegmentedControl from 'Components/DesignSystem/SegmentedControl/SegmentedControl';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import Tab from 'Components/DesignSystem/Tab/Tab';
import Tag from 'Components/DesignSystem/Tag/Tag';
import TextField from 'Components/DesignSystem/TextField/TextField';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Typography from 'Components/DesignSystem/Typography/Typography';
import {
    getChannelEmbedSettings,
    getISRProject,
    updateChannelEmbedSettings,
} from "../Pages/DefineModel/ModelServices/ModalServices";
import {
    buildParamSnippet,
    buildPlacementSnippet,
    buildSiteHtml,
    buildSiteScript,
    CATALOG_ENDPOINT_PLACEHOLDER,
    displayFor,
    DISPLAY_DESCRIPTION,
    DISPLAY_LABEL,
    EMBED_ENVIRONMENTS,
    IDENTIFIER_MODES,
    isHttpUrl,
    PLATFORM_DESCRIPTION,
    PLATFORM_PASTE_INSTRUCTIONS,
    PLATFORMS,
    SKU_NOTE,
    STARTING_STATES,
    TEmbedEnv,
    TEmbedParam,
    TEmbedWidget,
    TIdentifierMode,
    TPlatform,
    TRIGGERS,
    TStartingState,
    TTrigger,
} from "./embedSnippet";

type TEmbedTabId = 'site' | 'plc' | 'par';

type TEmbedCodeDialogProps = {
    id?: string;
    /** The channel key, which doubles as the merchant key in the embed snippet. */
    merchantKey: string;
    /** Scopes the persisted channel embed settings (ADO #14218 US-5) alongside merchantKey. */
    industry: string;
    /** Every widget in this channel — the Site code Script lists all of them. */
    widgets: TEmbedWidget[];
    initialTab?: TEmbedTabId;
    /**
     * Titles of widgets that were unviewed at the moment this dialog opened — a
     * snapshot taken BEFORE the caller clears the flag, so the Site code tab can
     * still name what changed on the very visit that clears it (ADO #14217 US-4
     * AC 3). Empty/omitted renders no change list.
     */
    newWidgetTitles?: string[];
};

const EmbedCodeDialog = ({ id, merchantKey, industry, widgets, initialTab = 'site', newWidgetTitles = [] }: TEmbedCodeDialogProps) => {
    const baseId = id || 'embed-code-dialog';

    const [activeTab, setActiveTab] = useState<TEmbedTabId>(initialTab);

    // Site code — channel-level embed settings (ADO #14218 US-5): Platform, Identifier
    // mode and Catalog endpoint persist per channel and reload on reopen. Environment is
    // deliberately NOT persisted (out of AC 1's scope) — always starts at Production.
    const [platform, setPlatform] = useState<TPlatform>('custom');
    const [envKey, setEnvKey] = useState<string>(EMBED_ENVIRONMENTS[0].key);
    const [identifierMode, setIdentifierMode] = useState<TIdentifierMode>('widgetId');
    const [catalogEndpoint, setCatalogEndpoint] = useState('');
    const [endpointError, setEndpointError] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const responseData = await getChannelEmbedSettings(industry, merchantKey);
            if (cancelled || !responseData?.done) return;
            const saved = responseData.data ?? {};
            if (saved.platform) setPlatform(saved.platform);
            if (saved.identifier_mode) setIdentifierMode(saved.identifier_mode);
            if (saved.catalog_endpoint) setCatalogEndpoint(saved.catalog_endpoint);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [industry, merchantKey]);

    const saveEmbedSettings = async (patch: { platform?: TPlatform; identifier_mode?: TIdentifierMode; catalog_endpoint?: string }) => {
        const responseData = await updateChannelEmbedSettings(industry, merchantKey, patch);
        if (!responseData?.done) {
            toast.error(responseData?.message ?? 'Failed to save embed settings');
        }
    };

    const changePlatform = (value: TPlatform) => {
        setPlatform(value);
        saveEmbedSettings({ platform: value });
    };

    const changeIdentifierMode = (value: TIdentifierMode) => {
        setIdentifierMode(value);
        saveEmbedSettings({ identifier_mode: value });
    };

    // Saved on blur, not per keystroke — a free-text field mid-type is neither
    // validate-able nor worth a request per character. Validated client-side first
    // (ADO #14218 US-5 AC 5); the server re-validates regardless. The Script uses the
    // same check, so a value this rejects prints the placeholder (ADO #15321).
    const handleEndpointBlur = () => {
        const trimmed = catalogEndpoint.trim();
        if (!trimmed) {
            setEndpointError('');
            saveEmbedSettings({ catalog_endpoint: '' });
            return;
        }
        if (!isHttpUrl(trimmed)) {
            setEndpointError('Enter a valid URL, e.g. https://yourshop.com/api/products');
            return;
        }
        setEndpointError('');
        saveEmbedSettings({ catalog_endpoint: trimmed });
    };

    // Widget placements — generator state only, never persisted.
    const [plcWidgetId, setPlcWidgetId] = useState<string>(widgets[0]?.id ?? '');
    const [trigger, setTrigger] = useState<TTrigger>('banner');
    const [startingState, setStartingState] = useState<TStartingState>('blank');

    // Input parameters, fetched per widget's model. A widget whose model
    // declares none does not appear on this tab at all.
    const [paramsByWidget, setParamsByWidget] = useState<Record<string, TEmbedParam[]>>({});
    const [paramsLoaded, setParamsLoaded] = useState(false);
    const [parWidgetId, setParWidgetId] = useState<string>('');

    const [copiedKey, setCopiedKey] = useState<string>('');

    useEffect(() => {
        let cancelled = false;
        setParamsLoaded(false);
        (async () => {
            const entries = await Promise.all(widgets.map(async (widget) => {
                try {
                    const responseData = await getISRProject(widget.modelId);
                    const params = responseData?.data?.project?.inputParameters ?? [];
                    return [widget.id, Array.isArray(params) ? params.filter((p: TEmbedParam) => p && p.paramKey) : []] as const;
                } catch (error) {
                    // Snippet is still useful without this widget's block; leave it empty.
                    return [widget.id, [] as TEmbedParam[]] as const;
                }
            }));
            if (!cancelled) {
                setParamsByWidget(Object.fromEntries(entries));
                setParamsLoaded(true);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [widgets.map((w) => w.modelId).join(',')]);

    const widgetsWithParams = useMemo(
        () => widgets.filter((w) => (paramsByWidget[w.id] ?? []).length > 0),
        [widgets, paramsByWidget]
    );

    // Keep each tab's selected widget valid as the widget list changes.
    useEffect(() => {
        if (widgets.length && !widgets.some((w) => w.id === plcWidgetId)) {
            setPlcWidgetId(widgets[0].id);
        }
    }, [widgets, plcWidgetId]);

    useEffect(() => {
        if (widgetsWithParams.length && !widgetsWithParams.some((w) => w.id === parWidgetId)) {
            setParWidgetId(widgetsWithParams[0].id);
        }
    }, [widgetsWithParams, parWidgetId]);

    const env: TEmbedEnv = EMBED_ENVIRONMENTS.find((e) => e.key === envKey) ?? EMBED_ENVIRONMENTS[0];

    const siteScript = buildSiteScript({ merchantKey, platform, env, identifierMode, catalogEndpoint, widgets });
    const siteHtml = buildSiteHtml();

    const plcWidget = widgets.find((w) => w.id === plcWidgetId);
    const placementSnippet = plcWidget
        ? buildPlacementSnippet({ widget: plcWidget, trigger, startingState, identifierMode, platform })
        : '';

    const parWidget = widgetsWithParams.find((w) => w.id === parWidgetId);
    const paramSnippet = parWidget ? buildParamSnippet(paramsByWidget[parWidget.id] ?? []) : '';

    const copy = async (text: string, key: string) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (error) {
            // clipboard API needs a secure context; fall back to execCommand.
            const textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        }
        setCopiedKey(key);
        window.setTimeout(() => setCopiedKey(''), 1600);
    };

    // Every tab's settings read as a label/value form — label at a fixed-width left
    // column, control + help text at right, one divider per row — not a stack of
    // top-labelled fields. `tw-pt-[7px]` lines the label up with the first line of
    // whatever sits beside it (a segmented control or an input), the way a form
    // label aligns with its field instead of floating above it.
    const fieldRow = (label: string, htmlId: string, control: React.ReactNode, help: React.ReactNode, opts: { last?: boolean; optional?: boolean } = {}) => (
        <div className={`tw-grid tw-grid-cols-[128px_1fr] tw-gap-x-[18px] tw-gap-y-1.5 tw-py-3.5 ${opts.last ? '' : 'tw-border-b tw-border-edge'}`}>
            <span className='tw-pt-[7px]'>
                <Typography id={htmlId} variant="sub-title-label" as="span" className="tw-text-ink tw-block">
                    {label}
                </Typography>
                {opts.optional && (
                    <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary tw-block">
                        Optional
                    </Typography>
                )}
            </span>
            <div className='tw-min-w-0'>
                {control}
                {help}
            </div>
        </div>
    );

    // `tw--mx-6` breaks out of the Dialog's own 24px content padding (§ AttributeValuesModal
    // uses the same pattern) so the header/pre/foot bands run edge-to-edge, then each band
    // re-adds that 24px as `tw-px-6` — which is what puts "Script"/"HTML" back flush with the
    // "Platform"/"Environment"/... labels above instead of sitting 12px further right inside
    // its own bordered card.
    const CodeBlock = ({ testId, label, sub, trailing, foot, code }: {
        testId: string;
        label: string;
        sub?: string;
        trailing?: React.ReactNode;
        foot?: React.ReactNode;
        code: string;
    }) => (
        <div className='tw--mx-6 tw-border-t tw-border-edge'>
            <div className='tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-px-6 tw-py-2 tw-border-b tw-border-edge'>
                <span className='tw-min-w-0'>
                    <Typography variant="title" as="span" className='tw-text-ink'>{label}</Typography>
                    {sub && (
                        <>
                            {' '}
                            <Typography variant="caption-metadata" as="span" className='tw-text-ink-secondary'>{sub}</Typography>
                        </>
                    )}
                </span>
                <span className='tw-ml-auto tw-flex tw-items-center tw-gap-2'>
                    {trailing}
                    <Button
                        id={`${testId}-copy-btn`}
                        data-testid={`${testId}-copy-btn`}
                        type="button"
                        variant="secondary"
                        size="sm"
                        icon={<Copy size={14} />}
                        onClick={() => copy(code, testId)}
                    >
                        {copiedKey === testId ? 'Copied' : 'Copy'}
                    </Button>
                </span>
            </div>
            <pre
                data-testid={`${testId}-block`}
                className='tw-bg-code-embed-surface tw-text-code-embed-ink tw-font-mono tw-px-6 tw-py-3 tw-text-xs tw-overflow-auto tw-max-h-64 tw-whitespace-pre tw-m-0'
            >
                {code}
            </pre>
            {foot && (
                <Typography
                    variant="caption-metadata"
                    as="p"
                    className='tw-text-ink-secondary tw-bg-surface-sunken tw-px-6 tw-py-2 tw-m-0 tw-border-t tw-border-edge'
                    data-testid={`${testId}-foot`}
                >
                    {foot}
                </Typography>
            )}
        </div>
    );

    const siteTab = (
        <div>
            {newWidgetTitles.length > 0 && (
                <Alert tone="warning" data-testid={`${baseId}-changes`} className='tw-mb-5'>
                    <strong>{newWidgetTitles.length} widget{newWidgetTitles.length > 1 ? 's' : ''} new since you last opened this.</strong>
                    {' '}Replace the Script block on your site with the version below.
                    <ul className='tw-list-disc tw-pl-5 tw-mt-1'>
                        {newWidgetTitles.map((title) => <li key={title}>{title}</li>)}
                    </ul>
                </Alert>
            )}

            {fieldRow(
                'Platform',
                `${baseId}-platform-label`,
                <SegmentedControl
                    ariaLabel="Platform"
                    fitContent
                    options={PLATFORMS.map((p) => ({ id: p.key, label: p.label }))}
                    value={platform}
                    onChange={(value) => changePlatform(value as TPlatform)}
                    data-testid={`${baseId}-platform`}
                />,
                <Typography variant="caption-metadata" as="p" className='tw-text-ink-secondary tw-mt-1.5'>
                    {PLATFORM_DESCRIPTION[platform]}
                </Typography>
            )}

            {fieldRow(
                'Environment',
                `${baseId}-env-label`,
                <SegmentedControl
                    ariaLabel="Environment"
                    fitContent
                    options={EMBED_ENVIRONMENTS.map((e) => ({ id: e.key, label: e.label }))}
                    value={envKey}
                    onChange={(value) => setEnvKey(String(value))}
                    data-testid={`${baseId}-env`}
                />,
                <Typography variant="caption-metadata" as="p" className='tw-text-ink-secondary tw-mt-1.5'>
                    {env.host}{envKey === 'staging' ? ' — a different domain from production, so saved builds do not carry across.' : ''}
                </Typography>
            )}

            {fieldRow(
                'Identifier',
                `${baseId}-identifier-label`,
                <SegmentedControl
                    ariaLabel="Identifier"
                    fitContent
                    options={IDENTIFIER_MODES.map((m) => ({ id: m.key, label: m.label }))}
                    value={identifierMode}
                    onChange={(value) => changeIdentifierMode(value as TIdentifierMode)}
                    data-testid={`${baseId}-identifier`}
                />,
                <Typography variant="caption-metadata" as="p" className='tw-text-ink-secondary tw-mt-1.5'>
                    {IDENTIFIER_MODES.find((m) => m.key === identifierMode)?.description}
                </Typography>
            )}

            {fieldRow(
                'Catalog endpoint',
                `${baseId}-endpoint-label`,
                <TextField
                    label=""
                    ariaLabel="Catalog endpoint"
                    placeholder={CATALOG_ENDPOINT_PLACEHOLDER[platform]}
                    value={catalogEndpoint}
                    onChange={setCatalogEndpoint}
                    onBlur={handleEndpointBlur}
                    error={!!endpointError}
                    helperText={endpointError || (platform === 'custom'
                        ? "Serve product data from your own API instead of Compatio’s catalog."
                        : `Required on ${PLATFORMS.find((p) => p.key === platform)?.label} — your product-catalog API.`)}
                    data-testid={`${baseId}-endpoint-input`}
                />,
                null,
                { last: true, optional: platform === 'custom' }
            )}

            <CodeBlock
                testId={`${baseId}-script`}
                label="Script"
                sub="— paste once where your scripts load; re-copy when widgets change"
                trailing={
                    <Tag
                        label={`${widgets.length} widget${widgets.length === 1 ? '' : 's'} listed`}
                        variant="meta"
                        data-testid={`${baseId}-widget-count`}
                    />
                }
                code={siteScript}
                foot={`${PLATFORM_PASTE_INSTRUCTIONS[platform]} Replace the placeholders, or drop the OPTIONAL lines. This block gains a dcConfig entry with every widget — everything else in it never changes.`}
            />

            <CodeBlock
                testId={`${baseId}-html`}
                label="HTML"
                sub="— copy once, ever"
                code={siteHtml}
                foot="One of each per page. These never change — if they are already on your site, do not paste them again."
            />
        </div>
    );

    const plcTab = (
        <div>
            {fieldRow(
                'Widget',
                `${baseId}-plc-widget-label`,
                <Dropdown
                    label=""
                    aria-label="Widget"
                    searchable
                    usePortal
                    options={widgets.map((w) => ({ value: w.id, label: w.title }))}
                    value={plcWidgetId || undefined}
                    onChange={(value) => setPlcWidgetId(String(value))}
                    helperText="Pick the widget this spot on the page should open."
                    data-testid={`${baseId}-plc-widget-select`}
                />,
                null
            )}

            {fieldRow(
                'Trigger',
                `${baseId}-trigger-label`,
                <SegmentedControl
                    ariaLabel="Trigger"
                    fitContent
                    options={TRIGGERS.map((t) => ({ id: t.key, label: t.label }))}
                    value={trigger}
                    onChange={(value) => setTrigger(value as TTrigger)}
                    data-testid={`${baseId}-trigger`}
                />,
                <Typography variant="caption-metadata" as="p" className='tw-text-ink-secondary tw-mt-1.5'>
                    {TRIGGERS.find((t) => t.key === trigger)?.description}
                </Typography>
            )}

            {fieldRow(
                'Display',
                `${baseId}-display-label`,
                <div className='tw-flex tw-items-center tw-gap-2'>
                    <Typography variant="body-primary" as="span" className='tw-font-medium' data-testid={`${baseId}-display-value`}>
                        {DISPLAY_LABEL[displayFor(trigger)]}
                    </Typography>
                    <Tag label="Set by trigger" variant="meta" data-testid={`${baseId}-display-tag`} />
                </div>,
                <Typography variant="caption-metadata" as="p" className='tw-text-ink-secondary tw-mt-1.5'>
                    {DISPLAY_DESCRIPTION[displayFor(trigger)]}
                </Typography>
            )}

            {fieldRow(
                'Starting state',
                `${baseId}-starting-state-label`,
                <SegmentedControl
                    ariaLabel="Starting state"
                    fitContent
                    options={STARTING_STATES.map((s) => ({ id: s.key, label: s.label }))}
                    value={startingState}
                    onChange={(value) => setStartingState(value as TStartingState)}
                    data-testid={`${baseId}-starting-state`}
                />,
                <Typography variant="caption-metadata" as="p" className='tw-text-ink-secondary tw-mt-1.5'>
                    {STARTING_STATES.find((s) => s.key === startingState)?.description}
                    {startingState === 'product' ? ` ${SKU_NOTE[platform]}` : ''}
                </Typography>,
                { last: !plcWidget }
            )}

            {plcWidget && (
                <CodeBlock
                    testId={`${baseId}-plc`}
                    label="Placement snippet"
                    sub={`— paste where the ${trigger === 'banner' ? 'banner' : trigger === 'button' ? 'button' : 'builder'} should appear`}
                    code={placementSnippet}
                    foot={trigger === 'banner'
                        ? 'The shared banner container — one per page, and every banner widget renders in it. If your Site code paste already put it where you want banners, there is nothing more to do.'
                        : trigger === 'none'
                        ? 'Needs the Site code on the page already — and, on its own, opens a full-screen modal like Banner and Button. To render in the page instead, also uncomment and configure embedMode in that Script block.'
                        : 'Needs the Site code on the page already.'}
                />
            )}
        </div>
    );

    const parTab = !paramsLoaded ? (
        <div className='tw-flex tw-justify-center tw-py-6'>
            <Spinner size="md" label="Loading..." showLabel data-testid={`${baseId}-par-loading`} />
        </div>
    ) : widgetsWithParams.length === 0 ? (
        <Alert tone="info" data-testid={`${baseId}-par-empty`}>
            No widget in this channel declares input parameters — nothing to add here. Parameters are
            declared on a model&rsquo;s Input Parameter tab in Guide, and widgets pointing at such a model
            appear on this tab automatically.
        </Alert>
    ) : (
        <div>
            {fieldRow(
                'Widget',
                `${baseId}-par-widget-label`,
                <Dropdown
                    label=""
                    aria-label="Widget"
                    searchable
                    usePortal
                    options={widgetsWithParams.map((w) => ({
                        value: w.id,
                        label: `${w.title} (${(paramsByWidget[w.id] ?? []).length})`,
                    }))}
                    value={parWidgetId || undefined}
                    onChange={(value) => setParWidgetId(String(value))}
                    helperText="Input parameters are declared on the model’s Input Parameter tab, so they differ per widget."
                    data-testid={`${baseId}-par-widget-select`}
                />,
                null,
                { last: !parWidget }
            )}

            {parWidget && (
                <CodeBlock
                    testId={`${baseId}-par`}
                    label="Input parameters"
                    sub="— paste after the site script"
                    code={paramSnippet}
                    foot="Replace each {{PLACEHOLDER}} with the value your page supplies. Needs the site Script on the page already."
                />
            )}
        </div>
    );

    // `panelId` + a hand-rolled tabpanel, not `tabs[].content`: when Tab owns the
    // content it wraps it in its own `tw-mt-4 tw-p-4 ... tw-border tw-rounded-srf`
    // card (built for a generic "boxed panel" consumer) — that stacked a SECOND
    // padded, bordered inset on top of the Dialog's own, so nothing in Site code
    // ever actually reached the Dialog's edge no matter how the tab's own content
    // was padded. `SidePanel` hits the same case and solves it the same way — see
    // its own `panelId` usage.
    const panelId = `${baseId}-panel`;
    return (
        // `tw--mt-5` cancels the Dialog content area's own top `tw-py-5` (it is
        // uniform on every Dialog, so it cannot be zeroed there) so the tab row
        // sits flush under the header rule instead of floating in an empty band —
        // the "padding before the tabs" the design has none of.
        <div id={baseId} data-testid={baseId} className='tw-text-ink tw--mt-5'>
            <Tab
                tabs={[
                    { id: 'site', label: 'Site code', htmlId: `${baseId}-tab-site`, 'data-testid': `${baseId}-tab-site` },
                    { id: 'plc', label: 'Widget placements', htmlId: `${baseId}-tab-plc`, 'data-testid': `${baseId}-tab-plc` },
                    { id: 'par', label: 'Input parameters', htmlId: `${baseId}-tab-par`, 'data-testid': `${baseId}-tab-par` },
                ]}
                activeTabId={activeTab}
                onChange={(tabId) => setActiveTab(tabId as TEmbedTabId)}
                panelId={panelId}
                data-testid={`${baseId}-tabs`}
            />
            <div
                id={panelId}
                role="tabpanel"
                aria-labelledby={`${baseId}-tab-${activeTab}`}
                tabIndex={0}
                className={`tw-mt-5 ${FOCUS_RING}`}
            >
                {activeTab === 'site' && siteTab}
                {activeTab === 'plc' && plcTab}
                {activeTab === 'par' && parTab}
            </div>
        </div>
    );
};

export default EmbedCodeDialog;
